/**
 * @licence Copyright © 2019 Mercury Redstone BV, all rights reserved
 */
import {
  forwardRef,
  useState,
  useMemo,
  useEffect,
  type ComponentProps,
} from 'react';
import styled, { css } from 'styled-components';
import { isNil } from 'lodash-es';
import { useTranslation } from 'react-i18next';
import { useMeasure } from 'react-use';
import ReactECharts from 'echarts-for-react';
import { colors } from '../../utils/colors';
import { getPrice, getPriceDifference } from '../../utils/currency';
import { semiBoldText } from '../../utils/fonts';
import { getPercentages } from '../../utils/percentages';
import { minPortfolioItemCostToRender } from '../../utils/portfolio';
import { useCombinedRef } from '../../hooks';
import { PortfolioCurrency, type DashboardDataQuery } from '../../apollo';
import { getColor, getDownMedia } from '../../styles';
import { usePortfolioCurrency } from '../../providers';
import { GraphIconSummary, PieIcon } from '../icons';
import { Text } from '../texts';

export type IndexPortfolioPieProps = {
  portfolio: DashboardDataQuery['getIndexPortfolio'];
  portfolioTotals: DashboardDataQuery['getPortfolioTotals'];
} & ComponentProps<typeof Wrapper>;

const IndexPortfolioPie = forwardRef<HTMLElement, IndexPortfolioPieProps>(
  ({ portfolio, portfolioTotals, ...props }, ref) => {
    const { t } = useTranslation();
    const { currency } = usePortfolioCurrency();

    const [measureRef, { width: wrapperWidth }] = useMeasure();
    const wrapperRef = useCombinedRef(ref, measureRef);

    const [totalTextFontSize, setTotalTextFontSize] =
      useState<Nullish<number>>(null);

    const { totalCost, change24Amount, change24Percent } = useMemo(() => {
      return currency === PortfolioCurrency.Eur
        ? {
            totalCost: portfolioTotals.eurCostTotal,
            change24Amount: portfolioTotals.eurCostChange24Total,
            change24Percent: portfolioTotals.eurCostChange24Percentage,
          }
        : {
            totalCost: portfolioTotals.usdCostTotal,
            change24Amount: portfolioTotals.usdCostChange24total,
            change24Percent: portfolioTotals.usdCostChange24Percentage,
          };
    }, [portfolioTotals, currency]);

    const totalCostRenderValue = useMemo(
      () =>
        getPrice(totalCost, {
          ...(currency !== PortfolioCurrency.Eur
            ? { currency: 'USD' }
            : undefined),
          maximumFractionDigits: 0,
        }),
      [totalCost, currency]
    );

    const chartOption = useMemo(() => {
      const chartData = portfolio
        .filter(({ cost }) => minPortfolioItemCostToRender <= cost)
        .map((item) => ({
          value: currency === PortfolioCurrency.Eur ? item.cost : item.usdCost,
          name: item.currency.title,
          itemStyle: { color: item.currency.piechartColor },
          percentage: (item as any).percentage,
        }));

      return {
        tooltip: {
          trigger: 'item',
          formatter: (params: any) => {
            const price = getPrice(
              params.value,
              currency !== PortfolioCurrency.Eur
                ? { currency: 'USD' }
                : undefined
            );
            const percent = getPercentages(params.data.percentage);
            return `<b>${params.name}</b>: ${price}; ${percent}`;
          },
        },
        series: [
          {
            type: 'pie',
            radius: ['70%', '90%'],
            avoidLabelOverlap: false,
            label: { show: false },
            emphasis: {
              scale: false,
            },
            data: chartData,
          },
        ],
      };
    }, [portfolio, currency]);

    const infoBlocksData = useMemo(
      () => [
        {
          data: {
            positiveValue: change24Amount > 0,
            valueToRender: getPriceDifference(
              change24Amount,
              currency !== PortfolioCurrency.Eur
                ? { currency: 'USD' }
                : undefined
            ),
          },
          icon: <PieIcon />,
          infoText: 'DASHBOARD_PAGE__summaryChangeValue',
        },
        {
          data: {
            positiveValue: change24Percent > 0,
            valueToRender: getPercentages(change24Percent, { withSign: true }),
          },
          icon: <GraphIconSummary />,
          infoText: 'DASHBOARD_PAGE__summaryChangePercent',
        },
      ],
      [currency, change24Amount, change24Percent]
    );

    useEffect(() => {
      setTotalTextFontSize(
        wrapperWidth
          ? Math.round(wrapperWidth * (totalCost >= 100000 ? 0.085 : 0.09))
          : null
      );
    }, [wrapperWidth, totalCost]);

    return (
      <Wrapper ref={wrapperRef} {...props}>
        <PieBlock>
          <ReactECharts
            style={{ height: '100%', width: '100%' }}
            option={chartOption}
          />
          <TotalText
            component={'p'}
            variant={'h1'}
            $fontSize={totalTextFontSize}
          >
            {totalCostRenderValue}
          </TotalText>
        </PieBlock>
        <InfoBlocks>
          {infoBlocksData.map(({ data, icon, infoText }) => (
            <InfoBlock key={infoText}>
              <InfoBlockValue $positiveValue={data.positiveValue}>
                {data.valueToRender}
              </InfoBlockValue>
              <InfoBlockIcon>{icon}</InfoBlockIcon>
              <InfoBlockDesc>{t(infoText)}</InfoBlockDesc>
            </InfoBlock>
          ))}
        </InfoBlocks>
      </Wrapper>
    );
  }
);

const Wrapper = styled('div')`
  display: flex;
  flex-direction: column;
`;

const PieBlock = styled.div`
  align-self: center;
  width: 70%;
  aspect-ratio: 1;
  position: relative;

  ${getDownMedia('sm')} {
    max-width: 90%;
  }
`;

const InfoBlocks = styled.div`
  margin-top: 30px;
  align-self: flex-start;
  display: grid;
  grid-template-columns: repeat(3, auto);
  grid-gap: 4px 4px;
  align-items: center;
`;

const InfoBlock = styled.div`
  display: contents;
`;

const InfoBlockValue = styled(Text)<{ $positiveValue?: boolean }>`
  ${semiBoldText};
  white-space: nowrap;
  line-height: 1.4;
  ${({ $positiveValue }) => {
    if (isNil($positiveValue)) return null;

    return css`
      color: ${$positiveValue ? colors.green : colors.red};
    `;
  }}
`;

const InfoBlockIcon = styled.div`
  margin: 0 10px;

  svg {
    width: 14px;
    height: 14px;
    fill: ${({ theme }) => theme.palette.secondary.main};
  }
`;

const InfoBlockDesc = styled(Text)`
  line-height: 1.4;
`;

const TotalText = styled(Text)<{
  $fontSize: Nullish<number>;
}>`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  pointer-events: none;
  font-size: ${({ theme, $fontSize }) =>
    $fontSize ? `${$fontSize}px` : theme.typography.pxToRem(30)};
  color: ${getColor('blue')};
`;

export { IndexPortfolioPie };
